import React, { useEffect, useMemo, useState } from 'react';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { getOfficeChildren } from "@shared/utils/OfficeHelper";
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { OfficeUsageContentWrapper } from "./OfficeUsageStructure";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { Moment } from "moment";
import { getOfficeOccupancyUsage, OfficeOccupancyUsage, toExcelDownload } from '@features/reporting/services/ReportingService';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { ProgressionLine } from '@shared/ui/atoms/ProgressionLine';
import { hexToRGBA } from "@shared/utils/RandomColour";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { tablet } from '@shared/ui/atoms/MediaQueries';

export function OfficeFloorOccupancy(props: Props) {
  const {fromDate, selectedOffice, toDate} = props;
  const allOffices = useSelector(selectAllOfficeEntities);
  const childOffices = useMemo(() => getOfficeChildren(props?.selectedOffice?.id, allOffices), [allOffices, props?.selectedOffice?.id]);
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OfficeOccupancyUsage[]>([]);

  useEffect(() => {
    if (fromDate && selectedOffice) {
      setLoading(true);
      setData([]);
      getOfficeOccupancyUsage(selectedOffice, fromDate, toDate)
        .then(result => setData(result))
        .finally(() => setLoading(false));
    }
  }, [fromDate, selectedOffice, toDate]);

  const onDownloadClicked = async () => {
    toExcelDownload(data, `${selectedOffice?.label} floor usage ${fromDate?.format('MMM YYYY')}.csv`)
  }

  if (childOffices.length === 0 || !fromDate) {
    return null;
  }

  return (
    <Wrapper>
      <ContentCard>
        <HeadlineSmall style={{marginBottom: 20}}>{t('reports-page.office-usage.occupancy')}</HeadlineSmall>
        {loading && <Center>
          <LoadingSpinner hideBorder={true} hideText={true} />
        </Center>}

        {data.map((row, key) => (<React.Fragment key={key}>
          <ProgressionLine fgColour={Colours.green}
                           showPillSummary={true}
                           bgColour={hexToRGBA(Colours.green, 0.1)}
                           title={row.name}
                           percentage={row.averageUsage / row.capacity} />

        </React.Fragment>))}
        <OutlineButton  style={{marginTop: 20}}
                        click={onDownloadClicked}
                        disabled={loading && data.length > 0}
                        text={'reports-page.office-usage.download-btn'} />
      </ContentCard>
    </Wrapper>
  )
}

interface Props {
  selectedOffice?: OfficeEntity;
  fromDate?: Moment;
  toDate?: Moment;
}

const Wrapper = styled.div`
  width: 100%;
  background-color: ${Colours.veryLightGrey};
  margin: 16px 0;
  @media (${tablet}) {
    padding: 32px 0;
  }
`

const ContentCard = styled(OfficeUsageContentWrapper)`
  border-radius: 16px;
  border: 1px solid var(--Neutrals-Light-Grey, #EDEDED);
  margin: 32px auto;
  background: var(--Neutrals-White, #FFF);
  padding: 12px 16px;
  @media (${tablet}) {
    padding: 48px 64px;
  }
`
